// filepath: apps/web/src/features/admin/PermissionsMatrixGrid.tsx
//
// FR-UI-ADM-1 — Pure render component for the role × entity-type × CRUD matrix.
//
// The grid renders one row per role, one column per entity type, plus
// a trailing platform-scope column. Each editable cell shows 4
// checkboxes (C/R/U/D) and a "*" toggle that flips all four.
//
// System-role rows render with `aria-readonly="true"` and disabled
// checkboxes (AC-1 visual stability). The platform-scope column
// renders as read-only with a "server-managed" tooltip (C-2
// platform-scope is server-managed, never client-editable).
//
// INV-9: no dangerouslySetInnerHTML anywhere. All server strings
// render as text nodes.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import type { PermissionsResponse, RoleResponse } from "./types";

/** CRUD actions rendered per cell. Order matters for AC-1 stability. */
export const CRUD_ACTIONS = ["create", "read", "update", "delete"] as const;
export type CrudAction = (typeof CRUD_ACTIONS)[number];

interface PermissionsMatrixGridProps {
  roles: RoleResponse[];
  permissions: Record<string, PermissionsResponse>;
  entityTypes: string[];
  /** Lookup callback: roleName, entityType, action, isChecked → void. */
  onCellToggle: (
    roleName: string,
    entityType: string,
    action: CrudAction,
    isChecked: boolean,
  ) => void;
  /** Read-only predicate for a role (system roles are read-only). */
  isReadOnlyRole: (roleName: string) => boolean;
  /** Always read-only: the "*" / "auth" platform-scope column. */
  isPlatformColumn: (entityType: string) => boolean;
  /** True if resolved environment is production (entire grid is read-only). */
  isProduction: boolean;
  /** Optional banner shown above the grid (e.g. "stale matrix" warning). */
  conflictBanner?: React.ReactNode;
}

/**
 * PermissionsMatrixGrid — the matrix render component.
 *
 * Pure presentation: state lives in PermissionsMatrixPage (the parent
 * owns the editable PermissionsResponse map keyed by role name).
 */
export function PermissionsMatrixGrid({
  roles,
  permissions,
  entityTypes,
  onCellToggle,
  isReadOnlyRole,
  isPlatformColumn,
  isProduction,
  conflictBanner,
}: PermissionsMatrixGridProps): React.ReactElement {
  const { t } = useTranslation();

  const isCellReadOnly = (roleName: string, entityType: string): boolean =>
    isProduction || isReadOnlyRole(roleName) || isPlatformColumn(entityType);

  return (
    <div className="space-y-4" data-testid="permissions-matrix-grid">
      {conflictBanner}
      <div className="overflow-x-auto">
        <table
          className="min-w-full border-collapse text-sm"
          aria-label={t("admin.matrix.title")}
        >
          <thead>
            <tr className="border-b border-border">
              <th className="px-3 py-2 text-left font-medium" scope="col">
                {t("admin.matrix.column.role")}
              </th>
              {entityTypes.map((entityType) => (
                <th
                  key={entityType}
                  className="px-3 py-2 text-left font-medium"
                  scope="col"
                  data-testid={`column-${entityType}`}
                >
                  {entityType}
                </th>
              ))}
              <th
                className="px-3 py-2 text-left font-medium"
                scope="col"
                data-testid="column-platform"
              >
                {t("admin.matrix.column.platform")}
              </th>
            </tr>
          </thead>
          <tbody>
            {roles.map((role) => {
              const perms = permissions[role.name]?.permissions ?? [];
              const rowReadOnly = isProduction || isReadOnlyRole(role.name);
              return (
                <tr
                  key={role.id}
                  className="border-b border-border"
                  data-testid={`row-${role.name}`}
                >
                  <th scope="row" className="px-3 py-2 text-left font-medium">
                    <div>
                      <div>{role.name}</div>
                      {rowReadOnly && (
                        <div className="text-xs text-muted-foreground">
                          {t("admin.matrix.system_role.readonly")}
                        </div>
                      )}
                    </div>
                  </th>
                  {entityTypes.map((entityType) => (
                    <td
                      key={entityType}
                      className="px-3 py-2"
                      data-testid={`cell-${role.name}-${entityType}`}
                    >
                      <CrudCell
                        roleName={role.name}
                        entityType={entityType}
                        grants={perms}
                        readOnly={isCellReadOnly(role.name, entityType)}
                        onToggle={onCellToggle}
                      />
                    </td>
                  ))}
                  <td
                    className="px-3 py-2 text-xs text-muted-foreground"
                    data-testid={`cell-${role.name}-platform`}
                  >
                    <span title={t("admin.matrix.column.platform")}>
                      {t("admin.matrix.column.platform")}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface CrudCellProps {
  roleName: string;
  entityType: string;
  grants: { entity_type: string; actions: string[] }[];
  readOnly: boolean;
  onToggle: (
    roleName: string,
    entityType: string,
    action: CrudAction,
    isChecked: boolean,
  ) => void;
}

/**
 * CrudCell — 4 C/R/U/D checkboxes plus an "all" toggle.
 *
 * Reads current state from the `grants` array (the parent owns the
 * editable matrix). On every checkbox change it forwards the
 * delta to `onToggle`. The "all" checkbox checks/unchecks all four.
 */
function CrudCell({
  roleName,
  entityType,
  grants,
  readOnly,
  onToggle,
}: CrudCellProps): React.ReactElement {
  // Find the grant whose entity_type === entityType.
  // The wire shape: grants = [{entity_type: "Invoice", actions: ["create","read"]}].
  // The action mapping is INDEPENDENT of entity_type — a single grant
  // can carry multiple actions for the same entity_type. We treat
  // each (entity_type, action) as a boolean.
  const currentActions = new Set<string>();
  for (const g of grants) {
    if (g.entity_type === entityType) {
      for (const a of g.actions) {
        currentActions.add(a);
      }
    }
  }

  const allChecked = CRUD_ACTIONS.every((a) => currentActions.has(a));

  return (
    <fieldset
      className="flex flex-wrap items-center gap-2"
      disabled={readOnly}
      aria-disabled={readOnly ? "true" : "false"}
    >
      <legend className="sr-only">
        {roleName} × {entityType}
      </legend>
      {CRUD_ACTIONS.map((action) => {
        const checked = currentActions.has(action);
        return (
          <label
            key={action}
            className="flex items-center gap-1 text-xs"
            data-testid={`checkbox-${roleName}-${entityType}-${action}`}
          >
            <input
              type="checkbox"
              checked={checked}
              disabled={readOnly}
              onChange={(e) =>
                onToggle(roleName, entityType, action, e.target.checked)
              }
              aria-label={`${roleName} ${entityType} ${action}`}
            />
            <span>{action.charAt(0).toUpperCase()}</span>
          </label>
        );
      })}
      <label
        className="flex items-center gap-1 text-xs"
        data-testid={`checkbox-${roleName}-${entityType}-all`}
      >
        <input
          type="checkbox"
          checked={allChecked}
          disabled={readOnly}
          onChange={(e) => {
            for (const action of CRUD_ACTIONS) {
              onToggle(roleName, entityType, action, e.target.checked);
            }
          }}
          aria-label={`${roleName} ${entityType} all`}
        />
        <span>*</span>
      </label>
    </fieldset>
  );
}
